import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/context/LanguageContext";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const siteUrl = window.location.hostname.includes("lovableproject.com")
      ? "https://bait-al-teeb-essence.lovable.app"
      : window.location.origin;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/reset-password`,
    });

    if (error) {
      toast({ title: t.forgotPassword.error, description: error.message, variant: "destructive" });
      setIsSubmitting(false);
      return;
    }

    setIsSent(true);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 pb-20 px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-serif mb-2">{t.forgotPassword.title}</h1>
            <p className="text-muted-foreground">{t.forgotPassword.subtitle}</p>
          </div>

          <div className="bg-card border border-border p-8 rounded-sm">
            {isSent ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-serif mb-2">{t.forgotPassword.sentTitle}</h3>
                <p className="text-muted-foreground mb-6">{t.forgotPassword.sentDesc}</p>
                <Link to="/login" className="text-primary hover:underline text-sm">
                  {t.forgotPassword.backToLogin}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground rtl:left-auto rtl:right-3" />
                  <Input
                    type="email"
                    placeholder={t.forgotPassword.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rtl:pl-3 rtl:pr-10"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <Button type="submit" variant="elegant" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.forgotPassword.sending}</>) : t.forgotPassword.sendBtn}
                </Button>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  <Link to="/login" className="text-primary hover:underline">{t.forgotPassword.backToLogin}</Link>
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
